import { useQuery } from '@tanstack/react-query'
import { CalendarOff, CheckCheck, Lock, Save, StickyNote, UsersRound } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { EmptyState, ErrorState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Alert } from '@/components/ui/Alert'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Skeleton } from '@/components/ui/Skeleton'
import { cn } from '@/utils/cn'
import { registerQuery, useMarkAttendance } from '../api'
import { formatLongDay } from '../dates'
import { countStatuses, formatRate, translateAttendanceMessage } from '../status'
import type { AttendanceStatus, Register } from '../types'
import { StatusSegmented } from './StatusSegmented'
import { SummaryChips } from './SummaryChips'

interface AttendanceRegisterProps {
  sectionId: number
  date: string
}

/** Loads the register of a section for a date and renders the editor (remounted per section/date). */
export function AttendanceRegister({ sectionId, date }: AttendanceRegisterProps) {
  const { t } = useTranslation('attendance')
  const register = useQuery(registerQuery(sectionId, date))

  if (register.isPending) return <RegisterSkeleton />
  if (register.error) {
    const e = toApiError(register.error)
    return (
      <Card>
        {e.status === 403 ? (
          <EmptyState
            icon={<Lock />}
            title={t("You can't open this register")}
            description={t(
              'Only the class teacher of this section or an administrator can take its attendance.',
            )}
          />
        ) : (
          <ErrorState
            onRetry={() => void register.refetch()}
            description={e.status === 422 ? translateAttendanceMessage(e.message) : undefined}
          />
        )}
      </Card>
    )
  }
  return <RegisterEditor key={`${sectionId}-${register.data.date}`} data={register.data} />
}

interface RowState {
  status: AttendanceStatus | null
  note: string
  noteOpen: boolean
}

function RegisterEditor({ data }: { data: Register }) {
  const { t } = useTranslation('attendance')
  const mark = useMarkAttendance(data.section.id)
  const [rows, setRows] = useState<Record<number, RowState>>(() =>
    Object.fromEntries(
      data.students.map((s) => [
        s.studentId,
        {
          // Students on approved leave start as "excused" on days that can still be edited.
          status: s.status ?? (s.leave && data.editable ? ('excused' as const) : null),
          note: s.note ?? '',
          noteOpen: !!s.note,
        },
      ]),
    ),
  )
  const [error, setError] = useState<string | null>(null)
  const locked = !data.editable

  const counts = useMemo(() => countStatuses(Object.values(rows).map((r) => r.status)), [rows])
  const dirty = data.students.some((s) => {
    const r = rows[s.studentId]
    return r && (r.status !== s.status || r.note.trim() !== (s.note ?? ''))
  })
  const unmarked = data.students.length - counts.marked

  const update = (id: number, patch: Partial<RowState>) =>
    setRows((prev) => {
      const current = prev[id] ?? { status: null, note: '', noteOpen: false }
      return { ...prev, [id]: { ...current, ...patch } }
    })

  const onLeave = new Set(data.students.filter((s) => s.leave).map((s) => s.studentId))
  const markRemainingPresent = () =>
    setRows((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([id, r]) => [
          id,
          r.status
            ? r
            : { ...r, status: onLeave.has(Number(id)) ? ('excused' as const) : ('present' as const) },
        ]),
      ),
    )

  const save = async () => {
    setError(null)
    const entries = data.students.flatMap((s) => {
      const r = rows[s.studentId]
      return r?.status ? [{ studentId: s.studentId, status: r.status, note: r.note.trim() || null }] : []
    })
    if (entries.length === 0) {
      setError(t('Mark at least one student before saving.'))
      return
    }
    try {
      const res = await mark.mutateAsync({ date: data.date, entries })
      const parts = [
        res.created ? t('{{count}} new', { count: res.created }) : '',
        res.updated ? t('{{count}} updated', { count: res.updated }) : '',
        res.unchanged ? t('{{count}} unchanged', { count: res.unchanged }) : '',
      ].filter(Boolean)
      toast.success(t('Attendance saved'), parts.join(' · '))
    } catch (e) {
      const apiError = toApiError(e)
      const violation = apiError.violations[0]
      setError(translateAttendanceMessage(violation ? violation.message : apiError.message))
    }
  }

  if (data.students.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<UsersRound />}
          title={t('No active students')}
          description={t('{{section}} has no active students enrolled this year.', {
            section: data.section.name,
          })}
        />
      </Card>
    )
  }

  const nonWorking = !data.workingDay.isWorkingDay
  const allUnmarked = counts.marked === 0

  return (
    <div className="space-y-4">
      {nonWorking ? (
        <Alert
          tone="warning"
          title={
            data.workingDay.holiday
              ? t('Holiday — {{name}}', { name: data.workingDay.holiday.name })
              : t('The school is closed on this day')
          }
        >
          {t('Attendance is only taken on working days. Pick another date to mark the register.')}
        </Alert>
      ) : locked && data.lockedReason ? (
        <Alert tone="warning" title={t('This register is read-only')}>
          {translateAttendanceMessage(data.lockedReason)}
        </Alert>
      ) : data.editWindowDays !== null ? (
        <p className="text-xs text-muted">
          {t('You can take or correct attendance for the last {{count}} days.', {
            count: data.editWindowDays,
          })}
        </p>
      ) : null}
      {error ? (
        <Alert tone="danger" title={t('Attendance was not saved')}>
          {error}
        </Alert>
      ) : null}

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-line px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h2 className="text-[0.9375rem] font-semibold">{data.section.name}</h2>
            <p className="mt-0.5 text-xs text-muted">
              {formatLongDay(data.date)} · {t('{{count}} students', { count: data.students.length })}
              {data.section.classTeacher
                ? ` · ${t('Class teacher {{name}}', { name: data.section.classTeacher })}`
                : ''}
            </p>
          </div>
          {!locked ? (
            <Button
              variant="soft"
              size="sm"
              leftIcon={<CheckCheck />}
              onClick={markRemainingPresent}
              disabled={unmarked === 0}
            >
              {allUnmarked ? t('Mark all present') : t('Mark remaining present')}
            </Button>
          ) : null}
        </div>

        <ul
          className="divide-y divide-line"
          aria-label={t('Register of {{section}}', { section: data.section.name })}
        >
          {data.students.map((s) => {
            const r = rows[s.studentId] ?? { status: null, note: '', noteOpen: false }
            return (
              <li key={s.studentId} className={cn('px-5 py-3', !r.status && !locked && 'bg-warning-50/40')}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <span className="w-7 shrink-0 text-right text-xs text-muted tabular">
                      {s.rollNumber ?? '—'}
                    </span>
                    <Avatar name={s.fullName} src={s.photoUrl} size="sm" />
                    <div className="min-w-0">
                      <p className="truncate font-medium text-ink">{s.fullName}</p>
                      <p className="text-xs text-muted">
                        {s.studentCode}
                        {!r.status ? ` · ${t('not marked')}` : ''}
                      </p>
                      {s.leave ? (
                        <Badge tone="info" size="sm" className="mt-1">
                          <CalendarOff aria-hidden="true" />
                          {t('On leave')} · {s.leave.type}
                          {s.leave.halfDay ? ` · ${t('half day')}` : ''}
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusSegmented
                      value={r.status}
                      onChange={(status) => update(s.studentId, { status })}
                      label={t('Attendance for {{name}}', { name: s.fullName })}
                      disabled={locked}
                    />
                    {!locked ? (
                      <IconButton
                        label={
                          r.noteOpen
                            ? t('Hide note for {{name}}', { name: s.fullName })
                            : t('Add a note for {{name}}', { name: s.fullName })
                        }
                        icon={<StickyNote />}
                        variant={r.note ? 'soft' : 'ghost'}
                        aria-expanded={r.noteOpen}
                        onClick={() => update(s.studentId, { noteOpen: !r.noteOpen })}
                      />
                    ) : null}
                  </div>
                </div>
                {r.noteOpen || (locked && r.note) ? (
                  <div className="mt-2 sm:pl-20">
                    <Input
                      size="sm"
                      aria-label={t('Note for {{name}}', { name: s.fullName })}
                      placeholder={t('Optional note, e.g. reason for absence')}
                      maxLength={255}
                      value={r.note}
                      readOnly={locked}
                      onChange={(e) => update(s.studentId, { note: e.target.value })}
                    />
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      </Card>

      <div className="sticky bottom-4 z-10">
        <Card className="flex items-center justify-between gap-3 px-4 py-3 shadow-raised sm:px-5">
          <div aria-live="polite" className="min-w-0">
            {/* Phones get a one-line summary so the bar never hides the roster. */}
            <p className="text-sm text-ink-soft sm:hidden">
              <span className="font-semibold text-ink tabular">
                {counts.marked}/{data.students.length}
              </span>{' '}
              {t('marked')} · {formatRate(counts.rate)}
            </p>
            <div className="hidden flex-wrap items-center gap-x-4 gap-y-2 sm:flex">
              <SummaryChips counts={counts} total={data.students.length} />
            </div>
          </div>
          {locked ? (
            <p className="flex items-center gap-1.5 text-xs text-muted">
              {nonWorking ? (
                <CalendarOff className="size-4" aria-hidden="true" />
              ) : (
                <Lock className="size-4" aria-hidden="true" />
              )}{' '}
              {t('Read-only · {{rate}} present', { rate: formatRate(counts.rate) })}
            </p>
          ) : (
            <Button
              leftIcon={<Save />}
              loading={mark.isPending}
              onClick={() => void save()}
              disabled={!dirty}
            >
              {dirty || data.summary.marked === 0 ? t('Save attendance') : t('Saved')}
            </Button>
          )}
        </Card>
      </div>
    </div>
  )
}

function RegisterSkeleton() {
  return (
    <Card className="divide-y divide-line" aria-busy="true">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-5 py-4">
          <Skeleton className="size-8 rounded-full" />
          <Skeleton className="h-3.5 w-40" />
          <Skeleton className="ml-auto h-10 w-72 max-w-1/2" />
        </div>
      ))}
    </Card>
  )
}
